import {ReadonlySignal} from "@preact/signals-react";
import {computed} from "@preact/signals-core";
import {s_char_attr_cha, s_char_attr_ges, s_char_attr_wah, s_char_attr_zae} from "./s_char_attr";
import {s_char, s_char_disciplines} from "./s_char";

import CharCreate from "../_helper/charcreate";
import calcSchwelle from "../_helper/calcSchwelle";
import updChar from "../_sig_api/updChar";
import {s_chars_mods} from "./s_chars_mods";
import {CharacterFieldUpdateField} from "../_payload/CharacterFieldUpdateField";

// see https://pandagaminggrove.blogspot.com/2015/02/earthdawn-4e-part-4-math-and-reducing.html
// Step Values: Attribute/3 (round up) + 1 [This was not changed]
// Defense Values: Attribute/2 (round up) +1
// Unconsciousness Rating: Toughness * 2
// Death Rating: Unconsciousness Rating + Toughness Step
// Wound Threshold: Toughness/2 (round up) + 2
// Recovery Tests: Toughness/6 (round up)
// Mystic Armor: Willpower/5 (round down)

export const modSum = (cid: string, mod: string) => {
    let modsSum = 0;
    if (s_chars_mods.value.has(cid)) {
        const charMods = Array.from(s_chars_mods.value.get(s_char.value.id)!.values());
        modsSum = charMods.filter(m => m.what === mod).reduce((s, m) => s + m.mmod, 0) ?? 0;
    }
    return modsSum
}

export const s_char_bs: ReadonlySignal<number> = computed(() => {
    return s_char.value.bew + modSum(s_char.value.id, 'bew')
});
export const s_char_ts: ReadonlySignal<number> = computed(() => {
    return s_char.value.tot + modSum(s_char.value.id, 'tot');
});
export const s_char_ws: ReadonlySignal<number> = computed(() => {
    return s_char.value.wund + modSum(s_char.value.id, 'wund');
});

export const s_char_ep: ReadonlySignal<number> = computed(() => {
    return s_char.value.ep + modSum(s_char.value.id, 'ep')
});

export const s_char_mr: ReadonlySignal<number> = computed(() => {
    return s_char.value.mr + modSum(s_char.value.id, 'mr')
});
export const s_char_pr: ReadonlySignal<number> = computed(() => {
    return s_char.value.pr + modSum(s_char.value.id, 'pr')
});

export const s_char_tr: ReadonlySignal<number> = computed(() => -12.5 * Math.ceil(s_char_attr_zae.value / 5) ^ 2 + 5 * Math.ceil(s_char_attr_zae.value / 5) * s_char_attr_zae.value + 12.5 * Math.ceil(s_char_attr_zae.value / 5) + 5)

export const s_char_bewusstlos: ReadonlySignal<boolean> = computed(() =>
    s_char.value.schaden + s_char.value.blutschaden > s_char_bs.value
);
export const s_char_tot: ReadonlySignal<boolean> = computed(() =>
    s_char.value.schaden + s_char.value.blutschaden > s_char_ts.value
);

export const healthBGC = () => {
    if (s_char.value.name !== '') {
        const health = s_char_ts.value
        const damage = s_char.value.schaden + s_char.value.blutschaden;
        return getGradientColor(health, damage);
    }
    return '#fff';
}

const getGradientColor = (totalValue: number, currentValue: number) => {
    if (!currentValue) return '#fff';
    const verlauf = ['#CCF80E', '#00DD00', '#1AE002', '#33E403', '#4DE705', '#66EB07', '#80EE09', '#99F10A', '#B3F50C', '#E6FC0F', '#FFFF11', '#FFFF11', '#FCE60F', '#F8CC0E', '#F5B30C', '#F1990A', '#EE8009', '#EB6607', '#E74D05', '#E43303', '#E01A02',];

    const color = Math.round(currentValue / totalValue * 20);

    if (color < 0) return '#fff';
    if (color > 20) return '#000';

    return verlauf[color];
}

/**
 * setzt schwellen, verteidigung, erholungsproben und mystische rüstung
 */
export const getBaseCharUpd = () => {
    let player = s_char.value
    const race = player.race;

    let upd: CharacterFieldUpdateField[] = []

    upd.push({
        k: 'str',
        v: Math.ceil((s_char.value.str_c + s_char.value.str_r + CharCreate.races[race]['str']) / 3 + 1)
    });
    upd.push({
        k: 'ges',
        v: Math.ceil((s_char.value.ges_c + s_char.value.ges_r + CharCreate.races[race]['ges']) / 3 + 1)
    });
    upd.push({
        k: 'zae',
        v: Math.ceil((s_char.value.zae_c + s_char.value.zae_r + CharCreate.races[race]['zae']) / 3 + 1)
    });
    upd.push({
        k: 'wah',
        v: Math.ceil((s_char.value.wah_c + s_char.value.wah_r + CharCreate.races[race]['wah']) / 3 + 1)
    });
    upd.push({
        k: 'wil',
        v: Math.ceil((s_char.value.wil_c + s_char.value.wil_r + CharCreate.races[race]['wil']) / 3 + 1)
    });
    upd.push({
        k: 'cha',
        v: Math.ceil((s_char.value.cha_c + s_char.value.cha_r + CharCreate.races[race]['cha']) / 3 + 1)
    });

    upd.push({k: 'kv', v: Math.ceil(s_char_attr_ges.value / 2) + 1});
    upd.push({k: 'mv', v: Math.ceil(s_char_attr_wah.value / 2) + 1});
    upd.push({k: 'sv', v: Math.ceil(s_char_attr_cha.value / 2) + 1});

    upd.push({k: 'ep', v: CharCreate.assign[s_char.value.zae].ep});

    upd.push({k: 'wund', v: CharCreate.assign[s_char.value.zae].ws});
    upd.push({k: 'bew', v: CharCreate.assign[s_char.value.zae].bs + calcSchwelle('bew')});
    upd.push({k: 'tot', v: CharCreate.assign[s_char.value.zae].ts + calcSchwelle('tot')});

    upd.push({k: 'mr', v: CharCreate.assign[s_char.value.wil].mr});

    const base = parseInt(CharCreate.races[race].karma);
    let highest = s_char_disciplines.value.reduce((max, d) => max = d.circle ?? 0 > max ? max = d.circle : max, 0);
    upd.push({k: 'karma', v: base * highest});

    return upd
}
